import { parse as parseUrl } from 'url'
import isUrl from 'is-url-superb'
import { useEffect, useState } from 'react'
import { kuiperClassNames } from '@opallabs/kuiper'
import { TextInput, TextInputValidationResult } from '@opallabs/lapidary'
import PreviewPane from './PreviewPane'
import * as styles from './styles'

const ALLOWED_DOMAINS = [
  'google.com',
  'live.com',
  'sharepoint.com',
  'sharepoint.us',
  'office.com',
  'miro.com',
  'figma.com',
  'canva.com',
  'box.com',
  'icloud.com',
  'dropbox.com',
  'linkedin.com',
  'youtube.com',
  'vimeo.com',
  'loom.com',
  'frame.io',
  'f.io',
]

type EmbedPreviewProps = {
  isEditing?: boolean
  onUrlChange: (value: string, valid: boolean) => void
  theme?: 'light' | 'dark'
  url: string | undefined
}

export default function EmbedPreview({
  isEditing = true,
  onUrlChange,
  theme = 'light',
  url,
}: EmbedPreviewProps) {
  const isValidDomain = (url: string) => {
    const { hostname } = parseUrl(url)
    if (hostname) {
      const domainParts = hostname.split('.')
      const topLevelDomain =
        domainParts.length > 2 ? domainParts.slice(-2).join('.') : hostname
      return ALLOWED_DOMAINS.includes(topLevelDomain)
    }
    return false
  }

  const [valid, setValid] = useState(
    typeof url === 'string' ? isValidDomain(url) && isUrl(url) : true
  )

  useEffect(() => {
    if (!url) {
      setValid(false)
    }
  }, [url])

  // One more level of validation to ensure the URL is valid before rendering the iframe
  const previewUrl = valid ? url : undefined

  const extractURL = (text: string) => {
    const urlPattern =
      /(\bhttps?:\/\/[-A-Z0-9+&@#/%?=~_|!:,.;{}]*[-A-Z0-9+&@#/%=~_|{}])/gi
    const matches = text.match(urlPattern)

    if (matches) {
      return matches.map((urlMatch) => {
        // Escape curly braces in the URL -- this fixes Sharepoint/Office365 embed URLs
        const escapedUrl = urlMatch.replace(/{/g, '%7B').replace(/}/g, '%7D')
        const { hostname, pathname } = parseUrl(escapedUrl)

        if (hostname) {
          // Handle YouTube Shorts URL
          if (
            hostname.endsWith('youtube.com') &&
            pathname &&
            pathname.includes('/shorts/')
          ) {
            const videoId = pathname.split('/shorts/')[1]
            return `https://www.youtube.com/embed/${videoId}`
          }

          // Handle Figma URL
          if (hostname.endsWith('figma.com')) {
            return `${escapedUrl}&embed_host=Opal`
          }

          // Handle iCloud and Canva URLs
          if (
            hostname.endsWith('icloud.com') ||
            hostname.endsWith('canva.com')
          ) {
            return `https://${hostname}${pathname}?embed=true`
          }
        }

        return urlMatch
      })[0]
    }

    return ''
  }

  const onInputChange = (value: string) => {
    const extractedURL = extractURL(value)
    if (isValidDomain(extractedURL) && isUrl(extractedURL)) {
      setValid(true)
      onUrlChange(extractedURL, true)
    } else if (value.length === 0) {
      setValid(true)
      onUrlChange(value, true)
    } else {
      setValid(false)
      onUrlChange(value, false)
    }
  }

  return (
    <div
      className={kuiperClassNames('embed-preview')}
      css={styles.wrapper}
      data-valid-url={valid}
    >
      <div css={styles.urlInput}>
        <div css={styles.urlInputField}>
          <TextInput
            clearable={true}
            disabled={!isEditing}
            theme={theme}
            value={url}
            placeholder="Paste embed code or URL"
            onChange={onInputChange}
            validator={(value: string): TextInputValidationResult => ({
              errorMessage: 'Invalid embed code or URL',
              valid:
                value.length == 0 ||
                (isValidDomain(extractURL(value)) && isUrl(extractURL(value))),
            })}
            styleOverrides={{
              container: { width: 900 },
              input: { paddingRight: valid ? 0 : 150 },
              message: {
                position: 'absolute',
                right: 50,
                top: 18,
              },
            }}
          />
        </div>
      </div>
      <PreviewPane isEditing={isEditing} url={previewUrl} />
    </div>
  )
}
